import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Globe, Sparkles, Trash2, Link2, Loader2, ClipboardPaste } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import WebReviews from "@/components/marketplace/WebReviews";

const label = (k) => (SOURCES.find((s) => s.key === k)?.label || k);
const SOURCES = [
  { key: "", label: "Auto-detect" },
  { key: "google", label: "Google" },
  { key: "facebook", label: "Facebook" },
  { key: "yelp", label: "Yelp" },
  { key: "trustpilot", label: "Trustpilot" },
  { key: "angi", label: "Angi" },
  { key: "thumbtack", label: "Thumbtack" },
  { key: "bbb", label: "BBB" },
  { key: "web", label: "Other website" },
];

export default function ExternalReviewsPage() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [source, setSource] = useState("");
  const [busy, setBusy] = useState(false);
  const [needsPaste, setNeedsPaste] = useState(false);
  const [pasted, setPasted] = useState("");
  const [pasteMsg, setPasteMsg] = useState("");

  const load = () => api.get("/reviews/external/mine").then(({ data }) => setItems(data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const runImport = async (usePaste) => {
    if (!url.trim() && !usePaste) { toast.error("Paste a review page link first"); return; }
    if (usePaste && !pasted.trim()) { toast.error("Paste the reviews text"); return; }
    setBusy(true);
    try {
      const body = { source_url: url.trim(), source: source || undefined };
      if (usePaste) body.pasted_text = pasted;
      const { data } = await api.post("/reviews/external/import", body);
      if (data.ok) {
        toast.success(data.record.reviews.length ? `Imported ${data.record.reviews.length} review(s) from ${label(data.record.source)}` : `Imported your ${label(data.record.source)} score — no review text was publicly visible`);
        setNeedsPaste(false); setPasted(""); setPasteMsg(""); setUrl("");
        load();
      } else if (data.needs_paste) {
        setNeedsPaste(true);
        setPasteMsg(data.message || "We couldn't read that page automatically. Please paste the reviews text.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || "Import failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this imported source and its reviews?")) return;
    try { await api.delete(`/reviews/external/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error("Failed to remove"); }
  };

  return (
    <DashboardShell>
    <div className="max-w-3xl" data-testid="external-reviews-page">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Reputation</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Import reviews from the web</h1>
        <p className="text-sm text-[#252A34]/70 mt-1">
          Paste a link to your Google, Yelp, Facebook (or other) reviews and our AI pulls in your latest 6 reviews and score.
          These show in a "Reviews from the web" section on your profile and don't change your GCW star rating.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div>
          <label className="text-xs font-medium text-[#252A34]/70">Review page link</label>
          <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 px-3">
            <Link2 className="h-4 w-4 text-[#252A34]/50 shrink-0" />
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.google.com/maps/place/your-business…"
              className="w-full py-2.5 text-sm outline-none bg-transparent"
              data-testid="ext-review-url"
            />
          </div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-[#252A34]/70">Source</label>
            <select value={source} onChange={(e) => setSource(e.target.value)} className="mt-1 block rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="ext-review-source">
              {SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <button
            onClick={() => runImport(false)}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] transition-colors disabled:opacity-50"
            data-testid="ext-review-import"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "AI is reading the reviews… (up to a minute)" : "Import with AI"}
          </button>
        </div>

        {needsPaste && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="ext-review-paste-block">
            <div className="flex items-start gap-2 text-sm text-amber-900">
              <ClipboardPaste className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{pasteMsg}</span>
            </div>
            <textarea
              value={pasted}
              onChange={(e) => setPasted(e.target.value)}
              rows={6}
              placeholder="Paste the reviews text you see on the page (author, stars, and comment for each review)…"
              className="mt-3 w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400"
              data-testid="ext-review-paste-text"
            />
            <button
              onClick={() => runImport(true)}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              data-testid="ext-review-paste-import"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Structure pasted reviews
            </button>
          </div>
        )}
      </div>

      <div className="mt-8">
        <h2 className="font-display text-lg font-semibold text-[#071b22] mb-4">Imported sources</h2>
        {items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-sm text-[#252A34]/70" data-testid="ext-review-empty">
            Nothing imported yet. Add a link above to showcase your reviews from around the web.
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((set) => (
              <div key={set.id} className="space-y-1" data-testid={`ext-review-item-${set.id}`}>
                <button
                  onClick={() => remove(set.id)}
                  className="ml-auto flex items-center gap-1 text-xs text-rose-600 hover:bg-rose-50 rounded-full px-2 py-1"
                  data-testid={`ext-review-remove-${set.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" /> Remove
                </button>
                <WebReviews items={[set]} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </DashboardShell>
  );
}
